import { Injectable } from '@angular/core';
import { ActivityRegisterState } from '@core/models/swagger/activityRegistry';
import { Mocks } from '@core/services/mocks/mocks';
import { environment } from '@environments/environment';
import { ApiService, DefaultHttpHeaders, HeadersEnum, HttpInputParametersType } from '@services/api/api.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, delay, finalize } from 'rxjs/operators';

type ActivityRegisterRequest = ActivityRegisterState;

@Injectable({
  providedIn: 'root',
})
export class ActivityRegistryService extends ApiService {
  mocked = false;

  private readonly loadingSubjects: Record<string, BehaviorSubject<boolean>> = {
    getInit: new BehaviorSubject<boolean>(false),
    postInsert: new BehaviorSubject<boolean>(false),
    getUpdateSession: new BehaviorSubject<boolean>(false),
  };

  readonly getInit$ = this.loadingSubjects.getInit.asObservable();
  readonly postInsert$ = this.loadingSubjects.postInsert.asObservable();
  readonly getUpdateSession$ = this.loadingSubjects.getUpdateSession.asObservable();

  public static readonly BASE_PATH = '/activity-registry';
  public static readonly INIT_PATH = '/init';
  public static readonly UPDATE_SESSION_PATH = '/update-session';
  public static readonly INSERT_PATH = '';

  private executePostRequest<TRequest, TResponse>({
    endpoint,
    body,
    requestKey,
    headers
  }: {
    endpoint: string;
    body: TRequest;
    requestKey: string;
    headers?: DefaultHttpHeaders;
  }): Observable<TResponse> {
    this.loadingSubjects[requestKey].next(true);

    if (this.mocked) {
      return Mocks[requestKey]().pipe(
        delay(2000),
        finalize(() => this.loadingSubjects[requestKey].next(false))
      );
    }

    return this.post<TRequest, TResponse>({
      apiUrl: `${environment.activityRegistryApiUrl}${ActivityRegistryService.BASE_PATH}`, suffix: endpoint, body, headers: {
        ...headers,
        [HeadersEnum.X_API_KEY]: environment.activityRegistryApiKey
      }
    }).pipe(
      finalize(() => this.loadingSubjects[requestKey].next(false))
    );
  }


  private executeGetRequest<TResponse>({
    endpoint,
    queryParams,
    requestKey,
    headers
  }: {
    endpoint: string;
    queryParams: HttpInputParametersType;
    requestKey: string;
    headers?: DefaultHttpHeaders;
  }): Observable<TResponse> {
    this.loadingSubjects[requestKey].next(true);

    if (this.mocked) {
      return Mocks[requestKey]().pipe(
        delay(2000),
        finalize(() => this.loadingSubjects[requestKey].next(false))
      );
    }

    return this.get<TResponse>({
      apiUrl: `${environment.activityRegistryApiUrl}${ActivityRegistryService.BASE_PATH}`, suffix: endpoint, queryParams, headers: {
        ...headers,
        [HeadersEnum.X_API_KEY]: environment.activityRegistryApiKey
      }
    }).pipe(
      finalize(() => this.loadingSubjects[requestKey].next(false))
    );
  }

  getInit(projectId: string): Observable<ActivityRegisterResponse> {
    return this.executeGetRequest<ActivityRegisterResponse>({
      endpoint: ActivityRegistryService.INIT_PATH,
      queryParams: {
        projectId: projectId
      },
      requestKey: 'getInit',
    }).pipe(
      // SessionId 0000 for errors
      catchError<ActivityRegisterResponse, Observable<ActivityRegisterResponse>>(_ => {
        return of<ActivityRegisterResponse>({
          name: '',
          message: '',
          sessionId: '0000'
        })
      }));
  }

  postInsert(body: ActivityRegisterRequest): Observable<ActivityRegisterInsertResponse> {
    return this.executePostRequest<ActivityRegisterRequest, ActivityRegisterInsertResponse>({
      endpoint: ActivityRegistryService.INSERT_PATH,
      body,
      requestKey: 'postInsert',
      headers: {
        [HeadersEnum.X_SESSION_ID]: body.sessionId
      },
    }).pipe(
      // return default response in case of errors
      catchError<ActivityRegisterInsertResponse, Observable<ActivityRegisterInsertResponse>>(_ => {
        return of<ActivityRegisterInsertResponse>({
          message: '',
          status: 1,
          response: {
            PublishResponse: {
              PublishResult: {
                MessageId: '',
                SequenceNumber: 1,
              },
              ResponseMetadata: {
                RequestId: '',
              },
            },
          },
        });
      })
    );
  }

  getUpdateSession(projectId: string, sessionId: string): Observable<ActivityRegisterResponse> {
    return this.executeGetRequest<ActivityRegisterResponse>({
      endpoint: ActivityRegistryService.UPDATE_SESSION_PATH,
      queryParams: {
        projectId: projectId,
        sessionId: sessionId
      },
      requestKey: 'getUpdateSession',
    }).pipe(
      // SessionId 0000 for errors
      catchError<ActivityRegisterResponse, Observable<ActivityRegisterResponse>>(_ => {
        return of<ActivityRegisterResponse>({
          name: '',
          message: '',
          sessionId: '0000'
        })
      }));
  }
}

export interface ActivityRegisterResponse {
  name: string;
  message: string;
  help?: string;
  activityId?: string;
  errors?: Error[];
  sessionId: string;
}

export interface ActivityRegisterInsertResponse {
  message: string;
  status: number;
  response: {
    PublishResponse: {
      PublishResult: {
        MessageId: string;
        SequenceNumber: number;
      };
      ResponseMetadata: {
        RequestId: string;
      };
    };
  };
}

interface Error {
  name: string;
  message: string;
  detail: Detail;
}

interface Detail {
  value: string;
  location: string;
  param: string;
}
